<script setup lang="ts" generic="T extends string | number">
import { computed, useId } from 'vue'

const props = defineProps<{
  label: string
  options: ReadonlyArray<{ value: T; label: string }>
  hint?: string
  error?: string
  compact?: boolean
  disabled?: boolean
  id?: string
}>()

const model = defineModel<T>({ required: true })

const generatedId = useId()
const selectId = computed(() => props.id ?? `select-${generatedId}`)
</script>

<template>
  <div class="ui-select">
    <label :for="selectId" class="ui-select__label" :class="{ 'ui-select__label--muted': compact }">{{ label }}</label>
    <select
      :id="selectId"
      v-model="model"
      class="ui-select__control"
      :class="{ 'ui-select__control--compact': compact, 'ui-select__control--invalid': !!error }"
      :aria-invalid="!!error || undefined"
      :disabled="disabled"
    >
      <option v-for="option in options" :key="String(option.value)" :value="option.value">{{ option.label }}</option>
    </select>
    <span v-if="error" role="alert" class="ui-select__error">{{ error }}</span>
    <span v-if="hint" class="ui-select__hint">{{ hint }}</span>
  </div>
</template>

<style scoped>
.ui-select {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.ui-select__label {
  font-size: var(--type-body-size);
  font-weight: var(--weight-medium);
  color: var(--text-label);
}
.ui-select__label--muted {
  font-weight: var(--weight-medium);
  color: var(--text-secondary);
}
.ui-select__control {
  height: var(--field-height);
  padding: 0 10px;
  border: 1px solid var(--opaque-separator);
  border-radius: var(--radius-field);
  font-size: var(--type-body-size);
  color: var(--text-primary);
  background: var(--surface-card)
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239E9E9E' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")
    no-repeat right 10px center;
  padding-right: 30px;
  appearance: none;
  cursor: pointer;
}
.ui-select__control--compact {
  height: var(--field-height-compact);
}
.ui-select__control--invalid {
  border-color: var(--error);
}
.ui-select__error {
  font-size: var(--type-footnote-size);
  color: var(--error);
}
.ui-select__hint {
  font-size: var(--type-footnote-size);
  color: var(--text-secondary);
}
.ui-select__control:focus {
  outline: none;
  border-color: var(--accent);
}
</style>
